import React, { useEffect, useState, useRef } from 'react';
import i18n from '../i18n';
import { AlertTriangle, TrendingDown } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

// ★ 2026-08-02 (Fase 30 F2) — Banner underperformance per Associate.
// Renderizzato in cima alla Dashboard. Se l'Associate ha una conversione
// Risposta→Show sotto target per 3 gg consecutivi:
//   • Mostra un banner rosso persistente
//   • Mostra un toast una sola volta al giorno (dedup lato backend)
//   • Il backend, la prima volta al giorno, invia anche un Telegram
//
// Per utenti non-Associate o senza dati sufficienti l'endpoint ritorna
// `underperforming=false` e il componente non renderizza nulla.

const LOCAL_SEEN_KEY = 'gp_underperf_toast_shown_';

export default function UnderperformanceAlertBanner() {
  const [payload, setPayload] = useState(null);
  const toastFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/kpis/underperformance-check');
        if (cancelled) return;
        setPayload(data);
        if (data?.underperforming && !toastFiredRef.current) {
          // Toast: al massimo 1/giorno per browser
          // ★ Audit sicurezza (Round 2): localStorage OK — flag UI non
          // sensibile ('1'), nessun dato personale/token. Deve persistere
          // tra sessioni per non ripetere il toast ogni login.
          const today = new Date().toISOString().slice(0, 10);
          const key = `${LOCAL_SEEN_KEY}${today}`;
          const alreadyShown = (() => {
            try { return localStorage.getItem(key) === '1'; } catch { return false; }
          })();
          if (!alreadyShown) {
            toast.warning(data.message || i18n.t('underperf.title', 'Conversione Risposta → Show sotto target'), {
              duration: 8000,
              description: i18n.t('underperf.toastDesc', 'Target: {{pct}}%. Rivedi le tue chiamate.', { pct: data.target_pct }),
              id: 'underperf-toast',
            });
            try { localStorage.setItem(key, '1'); } catch { /* no-op */ }
          }
          toastFiredRef.current = true;
        }
      } catch {
        // silenzioso — l'endpoint può 403 per non-agenti, ignoriamo
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!payload?.underperforming) return null;

  return (
    <Card
      className="border-2 border-amber-400 bg-amber-50/60 shadow-md"
      data-testid="underperformance-banner"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-amber-900">{i18n.t('underperf.title', 'Conversione Risposta → Show sotto target')}</h3>
              <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800">
                Target {payload.target_pct}%
              </Badge>
            </div>
            <p className="text-sm text-amber-900 mt-1">{payload.message}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(payload.days || []).map((d) => (
                <div
                  key={d.date}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] ${
                    d.below_target
                      ? 'border-amber-300 bg-white text-amber-800'
                      : 'border-slate-200 bg-white text-slate-600'
                  }`}
                  data-testid={`underperf-day-${d.date}`}
                >
                  <TrendingDown className={`w-3 h-3 ${d.below_target ? 'text-amber-600' : 'text-slate-400'}`} />
                  <span className="font-medium">{d.date.slice(5)}</span>
                  <span>·</span>
                  <span>{d.pct}% ({d.shows}/{d.responses})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
